/**
 * ValidationBadge - Phase 3: Validation Status Indicator
 *
 * Zeigt Validation Status für einen Channel an:
 * - Grün: Alle Bilder valide
 * - Rot: Validation Errors vorhanden
 * - Grau: Keine Requirements konfiguriert
 *
 * @accessibility WCAG 2.1 Level AA compliant
 */

import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils';

interface ValidationError {
  field: string;
  message: string;
  expected: string | null;
  actual: string | null;
}

interface ValidationResult {
  asset_id: string;
  is_valid: boolean;
  errors: ValidationError[];
}

interface ValidationResponse {
  success: boolean;
  is_valid: boolean;
  validation_results: ValidationResult[];
}

interface ValidationBadgeProps {
  productId: string;
  channelId: string;
  className?: string;
}

async function validateChannelImages(
  productId: string,
  channelId: string
): Promise<ValidationResponse> {
  const response = await fetch(
    `/api/v1/products/${productId}/channels/${channelId}/validate`
  );
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Validation fehlgeschlagen');
  }
  return data;
}

export function ValidationBadge({
  productId,
  channelId,
  className,
}: ValidationBadgeProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['channel-validation', productId, channelId],
    queryFn: () => validateChannelImages(productId, channelId),
    // Refetch when user returns to tab
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return null; // Don't show badge while loading
  }

  if (!data) {
    return null;
  }

  const errorCount = data.validation_results?.length || 0;
  const isValid = data.is_valid;

  // Has errors - red shield (WICHTIG: Diese Condition zuerst!)
  if (!isValid && errorCount > 0) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
          'cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors',
          className
        )}
        title={t('products.imagesFailRequirements', { count: errorCount })}
      >
        <ShieldAlert className="h-3.5 w-3.5" />
        <span>
          {t('products.validationErrors', { count: errorCount })}
        </span>
      </div>
    );
  }

  // All valid - green shield (nur wenn Requirements existieren und erfüllt)
  if (isValid && errorCount === 0) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          className
        )}
        title={t('products.allImagesValid')}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        <span>{t('products.valid')}</span>
      </div>
    );
  }

  // Fallback: Don't show badge
  return null;
}
